import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useApp } from "@/store/app";
import { fireConfetti } from "@/lib/confetti";
import { toast } from "sonner";
import { Plus, Users, X, Trophy, LogIn, ArrowRight, Loader2 } from "lucide-react";
import { compressImage, MAX_ORIGINAL_BYTES } from "@/lib/imageCompression";
import {
  useMyLeagues,
  useCreateLeague,
  useJoinLeague,
  useLeagueLeaderboard,
  type LeagueRow,
} from "@/hooks/useLeaguesDb";
import logo from "@/assets/logo.png";

import { useT } from "@/i18n";


export const Route = createFileRoute("/_app/leagues/")({
  component: LeaguesPage,
});

type LeagueCard = LeagueRow & { memberCount: number };

const GOLD = "#F5A623";

function LeaguesPage() {
  const t = useT();
  const user = useApp((s) => s.user)!;
  const { data: leagues = [], isLoading } = useMyLeagues(user?.id);
  const joinMutation = useJoinLeague();
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const navigate = useNavigate();

  const created = leagues.filter((l) => l.created_by === user.id && !l.is_protected);
  const joined = leagues.filter(
    (l) => l.created_by !== user.id || l.is_protected
  );

  function submitJoin(rawCode: string) {
    const code = rawCode.trim().toUpperCase();
    if (!code) return;
    joinMutation.mutate(
      { userId: user.id, code },
      {
        onSuccess: (league) => {
          toast.success(t(`Joined ${league.name}`, `Entrou em ${league.name}`));
          setShowJoin(false);
          navigate({ to: "/leagues/$code", params: { code: league.code } });
        },
        onError: (err: Error) => {
          if (err.message === "LEAGUE_NOT_FOUND") {
            toast.error(t("League not found", "Liga não encontrada"), {
              description: t(
                `Code ${code} doesn't match any league.`,
                `O código ${code} não corresponde a nenhuma liga.`
              ),
            });
          } else {
            toast.error(err.message || t("Could not join", "Não foi possível entrar"));
          }
        },
      }
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="mb-3 text-2xl font-bold tracking-tight">
          {t("My Leagues", "Grupele mele")}
        </h1>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl px-4 text-sm font-semibold text-black shadow-sm transition-opacity hover:opacity-90"
            style={{ background: `linear-gradient(135deg, ${GOLD}, #e8900a)` }}
          >
            <Plus className="h-4 w-4" />
            {t("Create league", "Creează grupă")}
          </button>
          <button
            onClick={() => setShowJoin(true)}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-border bg-background px-4 text-sm font-semibold text-foreground hover:bg-accent"
          >
            <LogIn className="h-4 w-4" />
            {t("Join League", "Intră în grupă")}
          </button>
        </div>
      </div>

      <Section
        title={t("Created by you", "Create de tine")}
        leagues={created}
        emptyText={t("You haven't created any league yet", "Nu ai creat nicio grupă încă")}
        emptyAction={{
          icon: <Plus className="h-4 w-4" />,
          label: t("Create league", "Creează grupă"),
          onClick: () => setShowCreate(true),
        }}
      />
      <Section
        title={t("Joined", "Participând")}
        leagues={[...joined].sort((a, b) =>
          a.code === "PPRO2026" ? -1 : b.code === "PPRO2026" ? 1 : 0
        )}
        emptyText={t("You haven't joined any league", "Nu ești în nicio grupă")}
        emptyAction={{
          icon: <ArrowRight className="h-4 w-4" />,
          label: t("Join league", "Intră în grupă"),
          onClick: () => setShowJoin(true),
        }}
      />

      {!isLoading && created.length === 0 && joined.length === 0 && (
        <div className="rounded-2xl border border-dashed py-16 text-center">
          <Trophy className="mx-auto h-12 w-12 text-premium opacity-80" />
          <p className="mt-3 font-semibold">{t("No leagues yet", "Nicio grupă încă")}</p>
        </div>
      )}

      {showCreate && (
        <CreateLeagueModal
          onClose={() => setShowCreate(false)}
          onCreated={(code) => {
            setShowCreate(false);
            navigate({ to: "/leagues/$code", params: { code } });
          }}
        />
      )}
      {showJoin && (
        <JoinLeagueModal
          onClose={() => setShowJoin(false)}
          onSubmit={submitJoin}
          loading={joinMutation.isPending}
        />
      )}
    </div>
  );
}


function Section({
  title,
  leagues,
  emptyText,
  emptyAction,
}: {
  title: string;
  leagues: LeagueCard[];
  emptyText?: string;
  emptyAction?: { icon: React.ReactNode; label: string; onClick: () => void };
}) {
  return (
    <div className="mb-6">
      <h2
        className="mb-3 inline-block border-l-2 pl-2 text-[13px] font-bold uppercase tracking-wider"
        style={{ color: GOLD, borderColor: GOLD }}
      >
        {title}
      </h2>
      {leagues.length === 0 ? (
        emptyText && (
          <div className="rounded-xl border border-dashed border-border/70 px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">{emptyText}</p>
            {emptyAction && (
              <button
                onClick={emptyAction.onClick}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
              >
                {emptyAction.icon}
                {emptyAction.label}
              </button>
            )}
          </div>
        )
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {leagues.map((l) => (
            <LeagueCardItem key={l.code} league={l} />
          ))}
        </div>
      )}
    </div>
  );
}

function rankCardClasses(pos: number | null) {
  if (pos === 1)
    return "border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.35),0_4px_20px_rgba(251,191,36,0.2)]";
  if (pos === 2)
    return "border-slate-400 shadow-[0_0_0_1px_rgba(148,163,184,0.35),0_4px_20px_rgba(148,163,184,0.15)]";
  if (pos === 3)
    return "border-orange-700 shadow-[0_0_0_1px_rgba(180,83,9,0.3),0_4px_20px_rgba(180,83,9,0.15)]";
  return "border-border/70 shadow-sm";
}

function rankPositionClass(pos: number | null) {
  if (pos === 1) return "text-amber-400";
  if (pos === 2) return "text-slate-400";
  if (pos === 3) return "text-orange-600";
  return "text-[#F5A623]";
}

function LeagueCardItem({ league: l }: { league: LeagueCard }) {
  const t = useT();
  const user = useApp((s) => s.user)!;
  const { data: leaderboard } = useLeagueLeaderboard(l.id);
  const total = leaderboard?.length ?? l.memberCount;
  const idx = leaderboard?.findIndex((r) => r.user_id === user.id) ?? -1;
  const position = leaderboard && idx >= 0
    ? leaderboard.filter((r) => r.points > leaderboard[idx].points).length + 1
    : null;
  const initial = (l.name?.trim().charAt(0) || "?").toUpperCase();
  const isTopRank = position !== null && position <= 3;

  return (
    <Link
      to="/leagues/$code"
      params={{ code: l.code }}
      className={`group block min-w-0 overflow-hidden rounded-xl border bg-gradient-to-br from-card to-card/60 p-4 transition-all hover:shadow-md hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-ring ${rankCardClasses(position)}`}
      style={isTopRank ? undefined : { borderLeft: `3px solid ${GOLD}` }}
    >
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        <div className="shrink-0">
          {l.is_protected ? (
            <img
              src={logo}
              alt=""
              className="h-12 w-12 object-contain"
            />
          ) : l.image_url ? (
            <img
              src={l.image_url}
              alt=""
              className="h-12 w-12 object-cover"
              onError={(e) => {
                const img = e.currentTarget;
                img.style.display = "none";
                const fallback = img.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-black"
            style={{
              background: `linear-gradient(135deg, ${GOLD}, #e8900a)`,
              display: l.is_protected || l.image_url ? "none" : "flex",
            }}
          >
            {initial}
          </div>
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-base font-bold text-foreground">{l.name}</span>
              </div>
              {l.is_protected ? (
                <>
                  <span
                    className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, #e8900a)` }}
                  >
                    🌍 {t("Global League", "Liga Globală")}
                  </span>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t("All platform players", "Toți jucătorii platformei")}
                  </p>
                </>
              ) : (
                l.display_creator_as && (
                  <div className="mt-0.5 text-[11px] font-medium text-muted-foreground">
                    {t("Created by", "Creat de")} {l.display_creator_as}
                  </div>
                )
              )}
            </div>
            {position !== null && (
              <div
                className="shrink-0 font-mono text-xs font-semibold tracking-wider"
                title={t("Position", "Poziție")}
              >
                <span className={rankPositionClass(position)}>#{position}</span>
                <span className="text-muted-foreground"> / {total}</span>
              </div>
            )}
          </div>

          <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-medium">
              <Users className="h-3 w-3" />
              {l.memberCount} {t("members", "membri")}
            </span>
            {!l.is_protected && (
              <span className="font-mono tracking-widest">{l.code}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}


function JoinLeagueModal({
  onClose,
  onSubmit,
  loading,
}: {
  onClose: () => void;
  onSubmit: (code: string) => void;
  loading?: boolean;
}) {
  const t = useT();
  const [code, setCode] = useState("");
  return (
    <ModalShell onClose={onClose} title={t("Join a league", "Entrar em uma liga")}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(code);
        }}
        className="space-y-4"
      >
        <Field label={t("League code", "Código da liga")}>
          <input
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={6}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-center text-lg font-bold uppercase tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>
        <button
          type="submit"
          disabled={!code.trim() || loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("Join league", "Entrar na liga")}
        </button>
      </form>
    </ModalShell>
  );
}

function CreateLeagueModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (code: string) => void;
}) {
  const tr = useT();
  const user = useApp((s) => s.user)!;
  const createMutation = useCreateLeague();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<string | undefined>(undefined);
  const [compressing, setCompressing] = useState(false);

  async function handleImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ORIGINAL_BYTES) {
      toast.error(tr("Image must be under 10MB", "Imaginea trebuie să fie sub 10MB"));
      return;
    }
    setCompressing(true);
    try {
      const dataUrl = await compressImage(file, { maxWidth: 800, maxHeight: 600, quality: 0.85 });
      setImage(dataUrl);
    } catch {
      toast.error(tr("Could not process image", "Nu am putut procesa imaginea"));
    } finally {
      setCompressing(false);
      e.target.value = "";
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate(
      {
        userId: user.id,
        name: name.trim(),
        description: description.trim(),
        imageUrl: image,
      },
      {
        onSuccess: (league) => {
          fireConfetti();
          toast.success(tr("League created!", "Liga criada!"), {
            description: tr(`Code: ${league.code}`, `Código: ${league.code}`),
          });
          onCreated(league.code);
        },
        onError: (err: Error) => {
          toast.error(err.message || tr("Could not create league", "Nu am putut crea liga"));
        },
      }
    );
  }

  return (
    <ModalShell onClose={onClose} title={tr("Create a league", "Criar uma liga")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={tr("League image (optional)", "Imagem da liga (opcional)")}>
          <div className="flex items-center gap-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-input bg-muted">
              {image ? (
                <img src={image} alt="" className="h-full w-full object-cover" />
              ) : (
                <Trophy className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <label className={`inline-flex items-center gap-1.5 cursor-pointer rounded-lg border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent ${compressing ? "pointer-events-none opacity-60" : ""}`}>
              {compressing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {compressing ? tr("Processing…", "Se procesează…") : (image ? tr("Change", "Alterar") : tr("Upload", "Enviar"))}
              <input type="file" accept="image/*" className="hidden" onChange={handleImage} disabled={compressing} />
            </label>
            {image && (
              <button
                type="button"
                onClick={() => setImage(undefined)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {tr("Remove", "Remover")}
              </button>
            )}
          </div>
        </Field>
        <Field label={tr("League name", "Nome da liga")}>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tr("e.g. Friends League", "", "ex. Liga Maeștrilor de fotbal")}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>
        <Field label={tr("Description (optional)", "Descrição (opcional)")}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>
        <button
          type="submit"
          disabled={!name.trim() || createMutation.isPending}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
        >
          {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {tr("Create league", "Criar liga")}
        </button>
      </form>
    </ModalShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

export function ModalShell({
  onClose,
  title,
  children,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 animate-pop">
      <div className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-accent">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
